// §0.5.6 Read-only flags display for billing team (ClaimReviewScreen).
// Shows active provider + system flags without add/remove controls.

import { Box, Text, VStack } from '@chakra-ui/react'
import { PROVIDER_FLAGS, ProviderFlag, User } from '@hb/shared'
import {
  collection,
  CollectionReference,
  DocumentReference,
  query,
  where,
} from 'firebase/firestore'
import { useMemo } from 'react'
import { useQuery } from '../../../hooks/backend/useQuery'

interface ReadOnlyPatientFlagsProps {
  patientRef: DocumentReference<User> | null
  assessmentId: string | null | undefined
  practiceId: string | null | undefined
}

const useReadOnlyFlags = (
  patientRef: DocumentReference<User> | null,
  assessmentId: string | null | undefined,
  practiceId: string | null | undefined,
) => {
  const ref = useMemo(() => {
    if (!assessmentId || !practiceId || !patientRef) return null
    const col = collection(patientRef, PROVIDER_FLAGS) as CollectionReference<ProviderFlag>
    return query(
      col,
      where('assessmentId', '==', assessmentId),
      where('practiceId', '==', practiceId),
    )
  }, [assessmentId, practiceId, patientRef])
  return useQuery<ProviderFlag>(ref)
}

export const ReadOnlyPatientFlags = ({
  assessmentId,
  practiceId,
  patientRef,
}: ReadOnlyPatientFlagsProps) => {
  const { dataArr } = useReadOnlyFlags(patientRef, assessmentId, practiceId)
  const activeFlags = useMemo(() => (dataArr ?? []).filter(f => !f.removedAt), [dataArr])
  if (activeFlags.length === 0) return null
  return (
    <Box
      bg="red.50"
      _dark={{ bg: 'red.900' }}
      borderRadius={6}
      border="1px solid"
      borderColor="red.200"
      px={3}
      py={2}>
      <Text fontSize="xs" fontWeight={700} color="red.700" letterSpacing="0.5px" mb={1.5}>
        PROVIDER FLAGS
      </Text>
      <VStack align="stretch" spacing={0.5}>
        {activeFlags.map(flag => (
          <Text key={flag.id} fontSize="xs" color="red.700" lineHeight={1.4}>
            {flag.text}
          </Text>
        ))}
      </VStack>
    </Box>
  )
}
